import { Layout, Skeleton } from 'antd';
import Logo from 'assets/logo.svg?react';
import HeaderWrap from '../Header';
import SideMenu from '../SideMenu';
import { DashboardProps } from './types';
import './dashboard.css';
import CompanyLogoIcon from 'assets/icons/CompanyLogo';
import RedirectIcon from 'assets/icons/RedirectIcon';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useUserInfo } from 'app/hooks/useUserInfo';
import { Header } from 'antd/lib/layout/layout';
import throttle from 'lodash/throttle';
import { useLocation, useNavigate } from 'react-router-dom';
import { PROFILE } from 'configs/routeNames';
import favicon from '/favicon.ico';
import { PERMISSIONS_SUBJECTS } from 'app/modules/Auth/constants';
import { usePermissions } from 'app/hooks/usePermissions';

const { Sider, Content } = Layout;

// Memoize static components
const MemoizedLogo = React.memo(Logo);
const MemoizedCompanyLogoIcon = React.memo(CompanyLogoIcon);
const MemoizedRedirectIcon = React.memo(RedirectIcon);

const DashboardLayout: React.FC<DashboardProps> = (props: DashboardProps) => {
  const { children } = props;
  const [isScrolled, setIsScrolled] = useState(false);
  const { data: currentUser, isLoading } = useUserInfo();

  const { agency } = currentUser || {};
  const agencyLogo = agency?.agencyLogo ?? '';
  const agencyName = agency?.agencyName ?? '';
  const agencyId = agency?.id ?? '';
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedMenuItem, setSelectedMenuItem] = useState<string>('sideMenu.home');
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLDivElement>(null);

  const { hasVisibility } = usePermissions();

  // Memoize agency data to prevent unnecessary re-renders
  const agencyData = useMemo(
    () => ({
      agencyLogo,
      agencyName,
      agencyId,
    }),
    [agencyLogo, agencyName, agencyId],
  );

  // Combine mobile-related effects
  useEffect(() => {
    const handleResize = throttle(() => {
      const width = window.innerWidth;
      const isMobileView = width < 992;

      if (isMobileView !== isMobile) {
        setIsMobile(isMobileView);
      }
    }, 150);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, [isMobile]);

  // Close menu on route change in mobile view
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [location.pathname, isMobile]);

  // Optimize scroll handler
  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrolled = window.scrollY > 2;
      if (isScrolled !== scrolled) {
        setIsScrolled(scrolled);
      }
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [isScrolled]);

  // Optimize outside click handler
  const handleOutsideClick = useCallback(
    (e: MouseEvent) => {
      if (!isMobile || collapsed) return;

      const sidebar = sidebarRef.current;
      const menuButton = menuButtonRef.current;
      const target = e.target as HTMLElement;

      // Don't close if clicking inside sidebar or on menu button
      if (sidebar && !sidebar.contains(target) && menuButton && !menuButton.contains(target)) {
        setCollapsed(true);
      }
    },
    [isMobile, collapsed],
  );

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [handleOutsideClick]);

  const handleSelectMenuItem = useCallback(
    (menuName: string) => {
      setSelectedMenuItem(menuName);
      if (isMobile) {
        setCollapsed(true);
      }
    },
    [isMobile],
  );

  // Memoize sidebar content
  const siderContent = useMemo(
    () => (
      <>
        <div className="flex items-center justify-center lg:py-10 py-5 px-5">
          {isLoading ? (
            <Skeleton.Input active className="!w-32 !h-10" />
          ) : agencyData.agencyLogo && !collapsed ? (
            <img
              loading="lazy"
              src={agencyData.agencyLogo}
              alt={
                agencyData?.agencyName?.length > 15
                  ? `${agencyData.agencyName.substring(0, 15)}...`
                  : agencyData.agencyName
              }
              className="max-w-full h-14"
            />
          ) : (
            !collapsed && <MemoizedCompanyLogoIcon className="text-neutral-primary" />
          )}
          {hasVisibility(PERMISSIONS_SUBJECTS.AGENCY) && (
            <MemoizedRedirectIcon
              className="text-neutral-primary gap-0 w-4 h-4 relative top-0.5 cursor-pointer"
              onClick={() => agencyData.agencyId && navigate(`${PROFILE}/agency`)}
            />
          )}
        </div>

        <SideMenu onMenuItemSelect={handleSelectMenuItem} collapsed={collapsed} />
        <div className="flex-shrink-0 lg:py-10 py-5 px-5 flex justify-center items-center">
          {collapsed ? (
            <img src={favicon} loading="lazy" alt="Logo" className="w-5 h-5" data-testid="poweredby" />
          ) : (
            <MemoizedLogo data-testid="poweredby" />
          )}
        </div>
      </>
    ),
    [agencyData, handleSelectMenuItem, navigate, collapsed],
  );

  // Memoize header props
  const headerProps = useMemo(
    () => ({
      setCollapsed,
      collapsed,
      setIsMobile,
      isMobile,
      selectedMenuItem,
    }),
    [collapsed, isMobile, selectedMenuItem],
  );

  return (
    <Layout hasSider>
      <Sider
        theme="light"
        className="app-sidebar"
        width={240}
        collapsedWidth={isMobile ? 0 : 80}
        collapsible
        trigger={null}
        collapsed={collapsed}
        ref={sidebarRef}
      >
        {siderContent}
      </Sider>

      <Layout className={`pb-5 lg:pb-10 ${isScrolled ? 'scroll-effect' : ''}`}>
        <Header
          ref={headerRef}
          className={`
            app-header
            h-auto
            px-5 lg:px-10
            transition-all duration-300 ease-in-out
            ${isScrolled ? '!py-5' : 'pt-5 lg:pt-10'}
          `}
        >
          <HeaderWrap {...headerProps} menuButtonRef={menuButtonRef} />
        </Header>
        <Content className="px-5 lg:px-10 pt-7 bg-neutral-background overflow-auto">{children}</Content>
      </Layout>
    </Layout>
  );
};

export default React.memo(DashboardLayout);
